import type {
  HumanReviewFocusFilters,
  HumanReviewTypedValue,
  HumanReviewViewFilters,
  HumanReviewViewState,
} from "../types"
import { normalizeHumanReviewSourceFilter } from "./humanReviewSourceFilter"
import {
  FILTER_FIELDS,
  FOCUS_FIELDS,
  ID_LISTS,
  MAX_BYTES,
  MAX_DISPLAY_METRICS,
  MAX_EXCLUDED_METADATA_VALUES,
  MAX_LIST,
  MAX_METADATA,
  MAX_TAGS,
  REVIEWER_LISTS,
  STRING_LISTS,
} from "./humanReviewViewStateContract"
import { normalizeProjectAnnotationsSort } from "./projectAnnotationsSort"

const RFC3339_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/
type JsonObject = Record<string, unknown>

function fail(message: string): never {
  throw new Error(`human_review_view: ${message}`)
}
function object(
  value: unknown,
  allowed: Set<string>,
  label: string,
): JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    fail(`${label} must be an object`)
  }
  const result = value as JsonObject
  for (const [key, item] of Object.entries(result)) {
    if (!allowed.has(key)) {
      fail(`${label} has unknown field ${key}`)
    }
    if (item === null) {
      fail(`${label} must not contain null fields`)
    }
  }
  return result
}
function text(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) {
    fail(`${label} must be non-blank`)
  }
  return value
}
function id(value: unknown, label: string): string {
  const result = text(value, label).trim()
  if (
    result === "." ||
    result === ".." ||
    result.includes("/") ||
    result.includes("?") ||
    result.includes("#") ||
    result.includes("%")
  ) {
    fail(`${label} must be a bare opaque id`)
  }
  return result
}

export function normalizeHumanReviewResourceId(value: unknown): string {
  return id(value, "resource_id")
}
function list(
  value: unknown,
  label: string,
  item: (entry: unknown) => string,
  cap = MAX_LIST,
  sort = true,
): string[] {
  if (!Array.isArray(value)) {
    fail(`${label} must be a list`)
  }
  if (value.length > cap) {
    fail(`${label} has too many values`)
  }
  const result = value.map(item)
  if (new Set(result).size !== result.length) {
    fail(`${label} must be distinct`)
  }
  return sort ? result.sort() : result
}
function timestamp(value: unknown, label: string): string {
  const raw = text(value, label)
  const match = RFC3339_PATTERN.exec(raw)
  if (!match) {
    fail(`${label} must be RFC3339 with a timezone`)
  }
  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const second = Number(secondText)
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysInMonth = [
    31,
    leapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month - 1]
  if (
    !daysInMonth ||
    day < 1 ||
    day > daysInMonth ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    fail(`${label} must be RFC3339`)
  }
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.valueOf())) {
    fail(`${label} must be RFC3339`)
  }
  return parsed.toISOString()
}
function typedValue(value: unknown, label: string): HumanReviewTypedValue {
  const raw = object(value, new Set(["kind", "value"]), label)
  if (raw.kind === "FLOAT") {
    if (typeof raw.value !== "number" || !Number.isFinite(raw.value)) {
      fail(`${label}.value must be finite`)
    }
    return { kind: "FLOAT", value: raw.value }
  }
  if (raw.kind === "STRING") {
    return { kind: "STRING", value: text(raw.value, `${label}.value`) }
  }
  if (raw.kind === "SET") {
    return {
      kind: "SET",
      value: list(raw.value, `${label}.value`, (item) =>
        text(item, `${label}.value`),
      ),
    }
  }
  fail(`${label}.kind is invalid`)
}
function normalizeFilters(value: unknown): HumanReviewViewFilters {
  const raw = object(value, FILTER_FIELDS, "filters")
  const filters: HumanReviewViewFilters = {}
  Object.assign(filters, normalizeHumanReviewSourceFilter(raw))
  const enums: Record<string, readonly string[]> = {
    assignment_status: ["PENDING", "COMPLETED", "SKIPPED"],
    label_state: ["LABELED", "UNLABELED"],
    triage_state: ["HAS_TRIAGE", "NO_TRIAGE"],
  }
  for (const [key, choices] of Object.entries(enums)) {
    if (key in raw) {
      if (typeof raw[key] !== "string" || !choices.includes(raw[key])) {
        fail(`filters.${key} is invalid`)
      }
      Object.assign(filters, { [key]: raw[key] })
    }
  }
  if ("conversation_review_progress" in raw) {
    const choices = ["NOT_STARTED", "IN_PROGRESS", "COMPLETE"]
    if (typeof raw.conversation_review_progress === "string") {
      if (!choices.includes(raw.conversation_review_progress)) {
        fail("filters.conversation_review_progress is invalid")
      }
      filters.conversation_review_progress =
        raw.conversation_review_progress as HumanReviewViewFilters["conversation_review_progress"]
    } else {
      const values = list(
        raw.conversation_review_progress,
        "filters.conversation_review_progress",
        (item) => {
          if (
            typeof item !== "string" ||
            !(choices as readonly string[]).includes(item)
          ) {
            fail("filters.conversation_review_progress is invalid")
          }
          return item
        },
        3,
      )
      if (values.length) {
        filters.conversation_review_progress =
          values as HumanReviewViewFilters["conversation_review_progress"]
      }
    }
  }
  for (const [key, choices, cap] of [
    ["reviewer_progress", ["PENDING", "COMPLETED"], 2],
    [
      "suspect_label_families",
      ["lone_positive", "peer_conflict", "machine_contradiction"],
      3,
    ],
  ] as const) {
    if (key in raw) {
      const values = list(
        raw[key],
        `filters.${key}`,
        (item) => {
          if (
            typeof item !== "string" ||
            !(choices as readonly string[]).includes(item)
          ) {
            fail(`filters.${key} is invalid`)
          }
          return item
        },
        cap,
      )
      if (values.length) {
        Object.assign(filters, { [key]: values })
      }
    }
  }
  for (const key of [
    "created_at_start",
    "created_at_end",
    "occurred_at_start",
    "occurred_at_end",
    "label_updated_at_start",
    "label_updated_at_end",
  ] as const) {
    if (key in raw) {
      filters[key] = timestamp(raw[key], `filters.${key}`)
    }
  }
  for (const [start, end] of [
    ["created_at_start", "created_at_end"],
    ["occurred_at_start", "occurred_at_end"],
    ["label_updated_at_start", "label_updated_at_end"],
  ] as const) {
    if (filters[start] && filters[end] && filters[start] >= filters[end]) {
      fail("filter start must be before filter end")
    }
  }
  for (const key of ID_LISTS) {
    if (key in raw) {
      const values = list(raw[key], `filters.${key}`, (item) =>
        id(item, `filters.${key}`),
      )
      if (values.length) {
        Object.assign(filters, { [key]: values })
      }
    }
  }
  for (const key of REVIEWER_LISTS) {
    if (key in raw) {
      if (!Array.isArray(raw[key])) {
        fail(`filters.${key} must be a list`)
      }
      if (raw[key].length > MAX_LIST) {
        fail(`filters.${key} has too many values`)
      }
      const values = Array.from(
        new Set(
          raw[key].map((item) =>
            text(item, `filters.${key}`).trim().toLowerCase(),
          ),
        ),
      ).sort()
      if (values.length) {
        Object.assign(filters, { [key]: values })
      }
    }
  }
  for (const key of STRING_LISTS) {
    if (key in raw) {
      const values = list(
        raw[key],
        `filters.${key}`,
        (item) => text(item, `filters.${key}`),
        key === "run_tags" ? MAX_TAGS : MAX_LIST,
      )
      if (
        key === "run_tags" &&
        new Set(
          values.map((item) =>
            item
              .normalize("NFKD")
              .toLowerCase()
              .replaceAll("ß", "ss")
              .replaceAll("ς", "σ"),
          ),
        ).size !== values.length
      ) {
        fail("filters.run_tags must be case-insensitively distinct")
      }
      if (values.length) {
        Object.assign(filters, { [key]: values })
      }
    }
  }
  if ("simulation_output_id_prefix" in raw) {
    const prefix = text(
      raw.simulation_output_id_prefix,
      "filters.simulation_output_id_prefix",
    )
    if ([...prefix].length > 22) {
      fail("filters.simulation_output_id_prefix is too long")
    }
    filters.simulation_output_id_prefix = prefix
  }
  if ("customer_metadata" in raw) {
    const metadata = object(
      raw.customer_metadata,
      new Set(Object.keys((raw.customer_metadata ?? {}) as JsonObject)),
      "filters.customer_metadata",
    )
    if (Object.keys(metadata).length > MAX_METADATA) {
      fail("filters.customer_metadata has too many entries")
    }
    // Object.create(null) avoids the `__proto__` key silently mutating the
    // object's prototype instead of becoming an own property.
    const normalized = Object.create(null) as Record<string, string>
    for (const [metadataKey, item] of Object.entries(metadata).sort(
      ([left], [right]) => (left < right ? -1 : left > right ? 1 : 0),
    )) {
      normalized[text(metadataKey, "metadata key")] = text(
        item,
        "metadata value",
      )
    }
    if (Object.keys(normalized).length) {
      filters.customer_metadata = normalized
    }
  }
  if ("excluded_customer_metadata" in raw) {
    const excludedMetadata = object(
      raw.excluded_customer_metadata,
      new Set(
        Object.keys((raw.excluded_customer_metadata ?? {}) as JsonObject),
      ),
      "filters.excluded_customer_metadata",
    )
    if (Object.keys(excludedMetadata).length > MAX_METADATA) {
      fail("filters.excluded_customer_metadata has too many entries")
    }
    // Object.create(null) avoids the `__proto__` key silently mutating the
    // object's prototype instead of becoming an own property (as above).
    const normalizedExcluded = Object.create(null) as Record<string, string[]>
    for (const [metadataKey, values] of Object.entries(excludedMetadata).sort(
      ([left], [right]) => (left < right ? -1 : left > right ? 1 : 0),
    )) {
      const key = text(metadataKey, "excluded metadata key")
      const normalizedValues = list(
        values,
        "excluded metadata values",
        (entry) => text(entry, "excluded metadata value"),
        MAX_EXCLUDED_METADATA_VALUES,
      )
      // An empty value list excludes nothing, so drop the key rather than
      // carry a no-op predicate into the request and the active-filter count.
      if (normalizedValues.length) {
        normalizedExcluded[key] = normalizedValues
      }
    }
    if (Object.keys(normalizedExcluded).length) {
      filters.excluded_customer_metadata = normalizedExcluded
    }
  }
  if ("focus" in raw) {
    const focusRaw = object(raw.focus, FOCUS_FIELDS, "filters.focus")
    const focus: HumanReviewFocusFilters = {}
    if ("metric_id" in focusRaw) {
      focus.metric_id = id(focusRaw.metric_id, "filters.focus.metric_id")
    }
    if (
      Object.keys(focusRaw).some((key) => key !== "metric_id") &&
      !focus.metric_id
    ) {
      fail("focus filters require focus.metric_id")
    }
    if ("answer_state" in focusRaw) {
      if (
        focusRaw.answer_state !== "ANSWERED" &&
        focusRaw.answer_state !== "UNANSWERED"
      ) {
        fail("filters.focus.answer_state is invalid")
      }
      focus.answer_state = focusRaw.answer_state
    }
    if ("human_value" in focusRaw) {
      focus.human_value = typedValue(
        focusRaw.human_value,
        "filters.focus.human_value",
      )
    }
    if ("machine_value" in focusRaw) {
      focus.machine_value = typedValue(
        focusRaw.machine_value,
        "filters.focus.machine_value",
      )
    }
    if ("machine_metric_version_ids" in focusRaw) {
      focus.machine_metric_version_ids = list(
        focusRaw.machine_metric_version_ids,
        "filters.focus.machine_metric_version_ids",
        (item) => id(item, "filters.focus.machine_metric_version_ids"),
      )
    }
    if ("agreement_state" in focusRaw) {
      const choices = [
        "NOT_REVIEWED",
        "NOT_COMPARABLE",
        "REVIEWER_CONFLICT",
        "HAS_DISAGREEMENT",
        "ALL_AGREE",
      ] as const
      if (
        !choices.includes(focusRaw.agreement_state as (typeof choices)[number])
      ) {
        fail("filters.focus.agreement_state is invalid")
      }
      focus.agreement_state =
        focusRaw.agreement_state as HumanReviewFocusFilters["agreement_state"]
    }
    if ("selected_comparison" in focusRaw) {
      const choices = [
        "HUMAN_YES_MACHINE_YES",
        "HUMAN_YES_MACHINE_NO",
        "HUMAN_NO_MACHINE_YES",
        "HUMAN_NO_MACHINE_NO",
        "AGREEMENT",
        "DISAGREEMENT",
        "NOT_REVIEWED",
        "NOT_COMPARABLE",
      ] as const
      if (
        !choices.includes(
          focusRaw.selected_comparison as (typeof choices)[number],
        )
      ) {
        fail("filters.focus.selected_comparison is invalid")
      }
      focus.selected_comparison =
        focusRaw.selected_comparison as HumanReviewFocusFilters["selected_comparison"]
    }
    if (Object.keys(focus).length) {
      filters.focus = focus
    }
  }
  if (
    filters.triage_state === "NO_TRIAGE" &&
    (filters.triage_values?.length || filters.triaged_by?.length)
  ) {
    fail("NO_TRIAGE cannot carry triage values or reviewers")
  }
  if (
    filters.label_state === "UNLABELED" &&
    (filters.labeled_by?.length ||
      filters.label_updated_at_start ||
      filters.label_updated_at_end ||
      filters.focus?.answer_state === "ANSWERED")
  ) {
    fail("UNLABELED cannot carry label filters or answered focus")
  }
  return filters
}

export function normalizeHumanReviewView(value: unknown): HumanReviewViewState {
  const raw = object(
    value,
    new Set(["v", "display_metric_ids", "filters", "sort"]),
    "human_review_view",
  )
  if (typeof raw.v !== "number" || !Number.isInteger(raw.v) || raw.v !== 1) {
    fail("v must be 1")
  }
  const state: HumanReviewViewState = { v: 1 }
  if ("display_metric_ids" in raw) {
    const values = list(
      raw.display_metric_ids,
      "display_metric_ids",
      (item) => id(item, "display_metric_ids"),
      MAX_DISPLAY_METRICS,
      false,
    )
    state.display_metric_ids = values
  }
  if ("filters" in raw) {
    const filters = normalizeFilters(raw.filters)
    if (Object.keys(filters).length) {
      state.filters = filters
    }
  }
  if ("sort" in raw) {
    const sort = normalizeProjectAnnotationsSort(raw.sort)
    if (sort) {
      state.sort = sort
    }
  }
  if (new TextEncoder().encode(JSON.stringify(state)).byteLength > MAX_BYTES) {
    fail("state exceeds 4096 UTF-8 bytes")
  }
  return state
}
